import { Input, Select, Space } from 'antd';
import { ProductState, ProductSaleType } from '@erpgold/shared';
import { useCategoryList } from '@/hooks/useCategories';
import { useBrandList } from '@/hooks/useBrands';
import { useFilterableAttributes } from '@/hooks/useFilterableAttributes';
import { FacetedFilter } from '@/components/FacetedFilter/FacetedFilter';
import type { ProductListQuery } from '@/services/queryKeys';

interface FiltersProps {
  value: ProductListQuery;
  onChange: (next: ProductListQuery) => void;
}

const stateOptions = [
  { value: ProductState.ACTIVE, label: 'Đang bán' },
  { value: ProductState.DRAFT, label: 'Nháp' },
  { value: ProductState.INACTIVE, label: 'Ngưng' },
];

const saleTypeOptions = [
  { value: ProductSaleType.ONLINE, label: 'Online' },
  { value: ProductSaleType.STORE, label: 'Tại cửa hàng' },
];

const sortOptions = [
  { value: 'name_asc', label: 'Tên A→Z' },
  { value: 'name_desc', label: 'Tên Z→A' },
  { value: 'created_at_desc', label: 'Mới nhất' },
  { value: 'sold_count_desc', label: 'Bán chạy' },
];

export function ProductFilters({ value, onChange }: FiltersProps) {
  const { data: catData } = useCategoryList({});
  const { data: brandData } = useBrandList({ page: 1, page_size: 200 });
  const { data: attributes } = useFilterableAttributes();

  return (
    <Space wrap>
      <Input.Search
        placeholder="Tìm theo tên / SKU"
        value={value.q}
        onChange={(e) => onChange({ ...value, q: e.target.value || undefined, page: 1 })}
        style={{ width: 280 }}
        allowClear
      />
      <Select<string[]>
        mode="multiple"
        placeholder="Danh mục"
        value={value.category_id}
        onChange={(v) => onChange({ ...value, category_id: v.length ? v : undefined, page: 1 })}
        options={(catData?.data ?? []).map((c) => ({ value: c._id, label: c.name }))}
        style={{ width: 220 }}
        maxTagCount="responsive"
        allowClear
      />
      <Select<string[]>
        mode="multiple"
        placeholder="Thương hiệu"
        value={value.brand_id}
        onChange={(v) => onChange({ ...value, brand_id: v.length ? v : undefined, page: 1 })}
        options={(brandData?.data ?? []).map((b) => ({ value: b._id, label: b.name }))}
        style={{ width: 220 }}
        maxTagCount="responsive"
        allowClear
      />
      <Select<number[]>
        mode="multiple"
        placeholder="Trạng thái"
        value={value.state}
        onChange={(v) => onChange({ ...value, state: v.length ? v : undefined, page: 1 })}
        options={stateOptions}
        style={{ width: 180 }}
        maxTagCount="responsive"
        allowClear
      />
      <Select<string[]>
        mode="multiple"
        placeholder="Kênh bán"
        value={value.sale_type}
        onChange={(v) => onChange({ ...value, sale_type: v.length ? v : undefined, page: 1 })}
        options={saleTypeOptions}
        style={{ width: 180 }}
        maxTagCount="responsive"
        allowClear
      />
      <Select
        placeholder="Sắp xếp"
        value={value.sort}
        onChange={(v) => onChange({ ...value, sort: v, page: 1 })}
        options={sortOptions}
        style={{ width: 160 }}
        allowClear
      />
      {(attributes ?? []).map((attr) => (
        <FacetedFilter
          key={attr.code}
          title={attr.name}
          options={attr.values}
          value={(value.attrs?.[attr.code] ?? []) as string[]}
          onChange={(selected) => {
            const newAttrs = { ...(value.attrs ?? {}) };
            if (selected.length > 0) {
              newAttrs[attr.code] = selected;
            } else {
              delete newAttrs[attr.code];
            }
            onChange({
              ...value,
              attrs: Object.keys(newAttrs).length > 0 ? newAttrs : undefined,
              page: 1,
            });
          }}
        />
      ))}
    </Space>
  );
}
